import React from 'react';
import type { FH110 } from '../../../services/pwh201Api';
import LookupInput from '../../../components/LookupInput';
import { PAYMENT_METHODS, INCOME_CATEGORIES, fmt, dateToInput, inputToDate, safeFloat } from '../helpers';

interface Tab2Props {
  formData: Partial<FH110>;
  names: Record<string, string>;
  isEditing: boolean;
  isLocked: boolean;
  setField: <K extends keyof FH110>(field: K, value: FH110[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  netTotal: number;
  taxTotal: number;
  grandTotal: number;
}

const Tab2PaymentInfo: React.FC<Tab2Props> = React.memo(({
  formData, names, isEditing, isLocked, setField, resolveSingleName,
  netTotal, taxTotal, grandTotal,
}) => {
  const isCheque = formData.fh110_27 === '03';
  const disabled = !isEditing || isLocked;

  return (
    <div className="pwh201-tab-content">
      {/* Row 1: Payee + Company + Department */}
      <div className="form-grid four-col">
        <label>受款人</label>
        <div className="lookup-field">
          <LookupInput
            lookupType="supplier"
            contractFieldId="fh110_15"
            value={formData.fh110_15 || ''}
            onChange={v => setField('fh110_15', v)}
            onCodeConfirm={code => resolveSingleName('payee', 'supplier', code)}
            disabled={disabled}
            maxLength={12}
          />
          <span className="lookup-name">{names.payee || ''}</span>
        </div>

        <label>公司代號</label>
        <div className="lookup-field">
          <LookupInput
            lookupType="supplier"
            contractFieldId="fh110_16"
            value={formData.fh110_16 || ''}
            onChange={v => setField('fh110_16', v)}
            onCodeConfirm={code => resolveSingleName('company', 'supplier', code)}
            disabled={disabled}
            maxLength={12}
          />
          <span className="lookup-name">{names.company || ''}</span>
        </div>

        <label>部門代號</label>
        <div className="lookup-field">
          <LookupInput
            lookupType="department"
            contractFieldId="fh110_37"
            value={formData.fh110_37 || ''}
            onChange={v => setField('fh110_37', v)}
            onCodeConfirm={code => resolveSingleName('department', 'department', code)}
            disabled={disabled}
            maxLength={10}
          />
          <span className="lookup-name">{names.department || ''}</span>
        </div>

        <label>幣別</label>
        <div className="lookup-field">
          <LookupInput
            lookupType="currency"
            contractFieldId="fh110_25"
            value={formData.fh110_25 || ''}
            onChange={v => setField('fh110_25', v)}
            onCodeConfirm={code => resolveSingleName('currency', 'currency', code)}
            disabled={disabled}
            maxLength={4}
          />
          <span className="lookup-name">{names.currency || ''}</span>
        </div>
      </div>

      {/* Row 2: Description */}
      <div className="form-grid two-col">
        <label>請款說明</label>
        <textarea
          data-contract-field="fh110_17"
          value={formData.fh110_17 || ''}
          onChange={e => setField('fh110_17', e.target.value)}
          disabled={disabled}
          rows={3}
        />
      </div>

      {/* Row 3: Payment method + Currency + Rate */}
      <div className="form-grid four-col">
        <label>票款別</label>
        <select
          data-contract-field="fh110_27"
          value={formData.fh110_27 || ''}
          onChange={e => setField('fh110_27', e.target.value)}
          disabled={disabled}
        >
          <option value="">-- 請選擇 --</option>
          {PAYMENT_METHODS.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>

        <label>匯率</label>
        <input
          data-contract-field="fh110_26"
          type="number"
          value={formData.fh110_26 ?? ''}
          onChange={e => setField('fh110_26', safeFloat(e.target.value) as never)}
          disabled={disabled}
          step="0.0001"
        />

        <label>所得類別</label>
        <select
          data-contract-field="fh110_48"
          value={formData.fh110_48 ?? ''}
          onChange={e => setField('fh110_48', parseInt(e.target.value) || 0 as never)}
          disabled={disabled}
        >
          <option value="">-- 無 --</option>
          {INCOME_CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <label>需要日期</label>
        <input data-contract-field="fh110_23"
          type="date"
          value={dateToInput(formData.fh110_23)}
          onChange={e => setField('fh110_23', inputToDate(e.target.value) as never)}
          disabled={disabled}
        />
      </div>

      {/* Row 4: Amounts (read-only computed) */}
      <div className="form-grid four-col">
        <label>原幣請款淨額</label>
        <input data-contract-field="fh110_12" value={fmt(netTotal)} readOnly disabled className="amount-field" />

        <label>總稅額</label>
        <input
          data-contract-field="fh110_28"
          type="number"
          value={formData.fh110_28 ?? ''}
          onChange={e => setField('fh110_28', safeFloat(e.target.value) as never)}
          disabled={disabled}
          className="amount-field"
        />

        <label>原幣請款總額</label>
        <input data-contract-field="fh110_13" value={fmt(grandTotal)} readOnly disabled className="amount-field" />

        <label>台幣請款總額</label>
        <input
          data-contract-field="fh110_13"
          value={fmt(grandTotal * (formData.fh110_26 || 1))}
          readOnly
          disabled
          className="amount-field"
          title="台幣請款總額＝原幣請款總額 FH110_13 × 匯率 FH110_26"
        />
      </div>

      {/* Row 5: Checkboxes + Dates */}
      <div className="form-grid four-col">
        <label data-contract-field="fh110_29">預計沖銷日</label>
        <input
          data-contract-field="fh110_29"
          type="date"
          value={dateToInput(formData.fh110_29)}
          onChange={e => setField('fh110_29', inputToDate(e.target.value) as never)}
          disabled={disabled}
        />

        <label className="checkbox-label" data-contract-field="fh110_242">
          <input
            data-contract-field="fh110_242"
            type="checkbox"
            checked={formData.fh110_242 || false}
            onChange={e => setField('fh110_242', e.target.checked as never)}
            disabled={disabled}
          />
          取消禁背
        </label>
        <div />

        <label className="checkbox-label" data-contract-field="fh110_42">
          <input
            data-contract-field="fh110_42"
            type="checkbox"
            checked={formData.fh110_42 || false}
            onChange={e => setField('fh110_42', e.target.checked as never)}
            disabled={disabled}
          />
          暫不付款
        </label>
        <div />

        <label className="checkbox-label" data-contract-field="fh110_44">
          <input
            data-contract-field="fh110_44"
            type="checkbox"
            checked={formData.fh110_44 || false}
            onChange={e => setField('fh110_44', e.target.checked as never)}
            disabled={disabled}
          />
          無憑證
        </label>
        <div />
      </div>

      {/* Cancel reason (only when 取消禁背 is checked) */}
      {formData.fh110_242 && (
        <div className="form-grid two-col">
          <label>取消理由</label>
          <textarea
            data-contract-field="fh110_43"
            value={formData.fh110_43 || ''}
            onChange={e => setField('fh110_43', e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>
      )}

      {/* Cheque fields (only when payment method = 03) */}
      {isCheque && (
        <>
          <div className="pwh201-section-title">支票資訊</div>
          <div className="form-grid four-col">
            <label>支票號碼(1)</label>
            <input
              data-contract-field="fh110_46"
              value={formData.fh110_46 || ''}
              onChange={e => setField('fh110_46', e.target.value)}
              disabled={disabled}
              maxLength={20}
            />

            <label>票面金額(1)</label>
            <input
              data-contract-field="fh110_31"
              type="number"
              value={formData.fh110_31 ?? ''}
              onChange={e => setField('fh110_31', safeFloat(e.target.value) as never)}
              disabled={disabled}
              className="amount-field"
            />

            <label>支票到期日(1)</label>
            <input
              data-contract-field="fh110_19"
              type="date"
              value={dateToInput(formData.fh110_19)}
              onChange={e => setField('fh110_19', inputToDate(e.target.value) as never)}
              disabled={disabled}
            />
            <div />

            <label>支票號碼(2)</label>
            <input
              data-contract-field="fh110_47"
              value={formData.fh110_47 || ''}
              onChange={e => setField('fh110_47', e.target.value)}
              disabled={disabled}
              maxLength={20}
            />

            <label>票面金額(2)</label>
            <input
              data-contract-field="fh110_32"
              type="number"
              value={formData.fh110_32 ?? ''}
              onChange={e => setField('fh110_32', safeFloat(e.target.value) as never)}
              disabled={disabled}
              className="amount-field"
            />

            <label>支票到期日(2)</label>
            <input
              data-contract-field="fh110_30"
              type="date"
              value={dateToInput(formData.fh110_30)}
              onChange={e => setField('fh110_30', inputToDate(e.target.value) as never)}
              disabled={disabled}
            />
            <div />
          </div>
        </>
      )}
    </div>
  );
});

Tab2PaymentInfo.displayName = 'Tab2PaymentInfo';
export default Tab2PaymentInfo;
